import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import type { Tagged } from "@-/com.npmjs.type-fest";
import { hasLength } from "@-/com.npmjs.ts-array-length";
import { createGate } from "@-/dev.improve.haniverse.lib.gate";
import { GraphemeMorphemePanelsContext } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._context";
import { usePrevalentPinyin } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.grapheme.pinyin.prevalence";

type MnemonicsTab = { name: string; pinyin: string | undefined; title: string };

export type MorphemeMnemonicsPanelTabButtonsGate = Tagged<
  { tabs: readonly MnemonicsTab[] },
  "MorphemeMnemonicsPanelTabButtonsGate"
>;

/**
 * Whether the mnemonics panel shows its primary navigation strip. `null` is the designed hidden state.
 *
 * `tabs` carries one entry per rendered screen — the meaning tab plus a transcription tab per study
 * reading. When pinyin mnemonics are disabled the view drops the pronunciation tabs, leaving only
 * "meaning"; a lone meaning button has nothing to switch to, so the whole bar hides rather than
 * showing a single dead tab.
 */
export function useMorphemeMnemonicsPanelTabButtonsGate(
  tabs: readonly MnemonicsTab[],
): MorphemeMnemonicsPanelTabButtonsGate | null {
  const [front, pinyinList] = useContextSelectors(
    GraphemeMorphemePanelsContext,
    (c) => c.readonlySlice.data.grapheme,
    (c) => c.readonlySlice.data.pinyinList,
  );
  const studyPinyinList = usePrevalentPinyin(front, pinyinList);
  const hasTranscriptionTab = tabs.some((tab) => tab.name !== "meaning");
  return !hasTranscriptionTab || hasLength(studyPinyinList, 0)
    ? null
    : createGate<MorphemeMnemonicsPanelTabButtonsGate>({ tabs });
}
